<template>
  <div class="w-full h-auto min-h-screen mt-3 justify-between bg-white space-y-9 rounded-t-[20px]">
    <HeaderComponent page-title="Learning Plans" />
    <div v-if="viewMode === 'list'" class="mx-8">
      <div class="flex items-center justify-between">
        <div class="space-y-3">
          <p class="font-semibold text-bodyText text-[20px]">Learning Plan Templates</p>
          <p class="text-bodyText text-[14px]">
            Create skill paths to assign to your team members
          </p>
        </div>

        <div class="flex gap-2">
          <button
            @click="viewMode = 'create'"
            class="flex bg-black text-primaryLight text-[14px] font-medium px-6 py-3 items-center rounded-[8px] h-[42px]"
          >
            Create New Template
          </button>
        </div>
      </div>
      
      <!-- List of templates -->
      <div class="mt-8">
        <div v-if="loading" class="flex justify-center py-20">
          <Loader text="Loading templates..." />
        </div>
        <EmptyState 
          v-else-if="templates.length === 0"
          title="No Learning Plans Yet"
          description="Create your first learning plan template to start assigning training modules to your team."
          :showIcon="false"
        >
        </EmptyState>
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="template in templates" :key="template.id" @click="goToDetails(template.id)" class="border border-gray-200 rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer relative group flex flex-col h-full">
            <div class="flex justify-between items-start gap-2 relative">
              <h3 class="font-semibold text-lg mb-2 flex-1 pr-2 line-clamp-2 min-h-[56px]">{{ template.topic }}</h3>
              
              <div class="flex items-center gap-2 flex-shrink-0">
                <span v-if="template.is_ready" class="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">Ready</span>
                <span v-else class="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">Generating...</span>

                <div class="relative z-10 -mr-2 flex items-center">
                  <button 
                    @click.stop="toggleDropdown(template.id)"
                    class="text-gray-400 hover:text-gray-600 transition-colors bg-white rounded-full p-1 flex items-center justify-center"
                  >
                    <MoreVertical class="w-5 h-5" />
                  </button>
                  <div v-if="openDropdownId === template.id" class="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg border border-gray-200 z-10 py-1">
                    <button @click.stop="openDeleteModal(template)" class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50 flex items-center gap-2">
                      <Trash2 class="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <p class="text-gray-600 text-sm mb-4 line-clamp-2 flex-1">{{ template.description }}</p>
            
            <div class="flex justify-between items-center mb-4 mt-auto text-xs text-gray-500">
              <span v-if="template.is_ready" class="bg-green-100 text-green-800 px-2 py-1 rounded">{{ template.task_count }} lessons</span>
              <span v-else></span>
              <span>{{ new Date(template.created_at).toLocaleDateString() }}</span>
            </div>
            
            <button 
              @click.stop="openAssignModal(template)"
              :disabled="!template.is_ready"
              class="w-full py-2 border border-black text-black rounded-md hover:bg-black hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Assign to Team
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Wizard View -->
    <div v-else-if="viewMode === 'create'" class="mx-8 pb-10">
      <CreateTemplateForm
        :teamId="selectedTeamId"
        @close="viewMode = 'list'"
        @success="handleWizardSuccess"
      />
    </div>

    <!-- Modals -->

    <DeleteProjectModal
      :isOpen="isDeleteModalOpen"
      :projectId="templateToDelete?.id || ''"
      :projectName="templateToDelete?.topic || ''"
      @close="isDeleteModalOpen = false"
      @confirm-delete="executeDelete"
    />

    <AssignTemplateModal
      :showModal="showAssignModal"
      :teamId="selectedTeamId"
      :template="selectedTemplate"
      @close="showAssignModal = false"
      @template-assigned="handleTemplateAssigned"
    />
  </div>
</template>

<script>
import HeaderComponent from "../HeaderComponent.vue";
import apiClient from "@/axios";
import CreateTemplateForm from "./CreateTemplateForm.vue";
import AssignTemplateModal from "../modals/AssignTemplateModal.vue";
import DeleteProjectModal from "../modals/DeleteProjectModal.vue";
import Loader from "@/components/ui/Loader.vue";
import EmptyState from "@/components/ui/EmptyState.vue";
import { MoreVertical, Trash2 } from "lucide-vue-next";

export default {
  name: "LearningPlansComponent",
  components: {
    HeaderComponent,
    CreateTemplateForm,
    AssignTemplateModal,
    DeleteProjectModal,
    MoreVertical,
    Trash2,
    Loader,
    EmptyState
  },
  data() {
    return {
      templates: [],
      loading: true,
      teams: [],
      selectedTeamId: null,
      viewMode: 'list', // 'list' | 'create'
      showAssignModal: false,
      selectedTemplate: null,
      isDeleteModalOpen: false,
      templateToDelete: null,
      openDropdownId: null,
    };
  },
  mounted() {
    this.fetchTeamsAndTemplates();
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    async fetchTeamsAndTemplates() {
      try {
        this.loading = true;
        // First get teams for the manager
        const response = await apiClient.get("/web/teams/manager/teams");
        const teamsData = response.data.data;
        if (teamsData && teamsData.length > 0) {
            this.teams = teamsData;
            // Handle both id and team_id based on backend schema variations
            this.selectedTeamId = teamsData[0].id || teamsData[0].team_id;
            await this.fetchTemplates(this.selectedTeamId);
        } else {
            this.loading = false;
        }
      } catch (error) {
        console.error("Error fetching teams:", error);
        this.loading = false;
      }
    },
    async fetchTemplates(teamId) {
      try {
        const response = await apiClient.get(`/web/teams/${teamId}/templates`);
        this.templates = response.data.data || [];
      } catch (error) {
        console.error("Error fetching templates:", error);
      } finally {
        this.loading = false;
      }
    },
    openAssignModal(template) {
        this.selectedTemplate = template;
        this.showAssignModal = true;
    },
    handleWizardSuccess() {
      this.viewMode = "list";
      this.fetchTemplates(this.selectedTeamId);
    },
    goToDetails(templateId) {
      this.$router.push(`/learning-plans/${templateId}`);
    },
    toggleDropdown(id) {
      this.openDropdownId = this.openDropdownId === id ? null : id;
    },
    handleClickOutside() {
      this.openDropdownId = null;
    },
    openDeleteModal(template) {
      this.openDropdownId = null;
      this.templateToDelete = template;
      this.isDeleteModalOpen = true;
    },
    async executeDelete() {
      if (!this.templateToDelete) return;
      try {
        const teamId = this.selectedTeamId;
        await apiClient.delete(`/web/teams/${teamId}/templates/${this.templateToDelete.id}`);
        this.templates = this.templates.filter(t => t.id !== this.templateToDelete.id);
        this.isDeleteModalOpen = false;
        this.templateToDelete = null;
      } catch (error) {
        console.error("Failed to delete template:", error);
        alert(error.response?.data?.message || "Failed to delete template");
      }
    },
    handleTemplateAssigned() {
        this.showAssignModal = false;
    }
  }
};
</script>

<style scoped>
</style>
